import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { PERMISSION } from "../../../enums/permissions.enum";

@Injectable({
  providedIn: "root",
})
export class PermissionService {
  private userPermissions = signal<string[]>([]);
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Sets the user permissions.
   * @param permissions An array of permission strings to be set for the user.
   */
  public setUserPermissions(permissions: string[]) {
    this.userPermissions.set(permissions);
  }

  /**
   * Checks if the user has one of the given permissions.
   * @param permission The permission to be checked. Can be a string or array of strings
   * @returns True if the user has the given permission, false otherwise.
   */
  public hasPermission(permission: PERMISSION | PERMISSION[]): boolean {
    const permissions = typeof permission === "string" ? [permission] : permission;

    for (const p of permissions) {
      if (this.userPermissions().includes(p)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the user is missing the given permission.
   * @param permission The permission string to be checked.
   * @returns True if the user is missing the given permission, false otherwise.
   */
  public missesPermission(permission: PERMISSION): boolean {
    return !this.hasPermission(permission);
  }

  /**
   * Fetches the list of remote permissions from the server.
   * @returns An Observable containing the canonical permission names.
   */
  public getRemotePermissions() {
    return this.http
      .get<string[]>(environment.baseUrlV2 + "/permission")
      .pipe(this.apiErrorHandler.handle("permission"));
  }

  /**
   * A dictionary of all permissions.
   * @remarks This is a static reference to the {@link PERMISSION} enum.
   * @returns A dictionary of all permissions.
   */
  get allPermissions() {
    return PERMISSION;
  }
}
